// src/components/MainComponent.js

import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TokenInputModal from './TokenInputModal';
import { ErrorDialog } from './ErrorBoundary';
import AppHeader from './AppHeader';
import AppSearch from './AppSearch';
import ResultDisplay from './ResultDisplay';
import LoadingProgress from './LoadingProgress';
import { HomeIcon, ChartBarIcon, DocumentIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { getCurrentApp, getToken, saveToken, removeToken, checkToken, processQuery } from '../services/appService';

const MainComponent = () => {
    const [token, setToken] = useState('');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [credits, setCredits] = useState(0);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [inputValues, setInputValues] = useState({});
    const [response, setResponse] = useState(null);
    const [timer, setTimer] = useState(0);
    const [creditsUsed, setCreditsUsed] = useState(0);
    
    // Get the current app configuration
    const currentApp = getCurrentApp();

    useEffect(() => {
        const initializeApp = async () => {
            const storedToken = getToken();
            if (storedToken) {
                setToken(storedToken);
                await validateTokenAndGetCredits(storedToken);
            }
            setLoading(false);
        };

        initializeApp();
    }, []);

    // Initialize input values based on current app's inputs
    useEffect(() => {
        if (currentApp && currentApp.inputs) {
            const initialValues = {};
            currentApp.inputs.forEach(input => {
                initialValues[input.key] = '';
            });
            setInputValues(initialValues);
        }
    }, [currentApp]);

    // Timer for estimated processing time
    useEffect(() => {
        let interval = null;
        
        if (isProcessing && timer > 0) {
            interval = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1);
            }, 1000);
        } else if (timer <= 0 && interval) {
            clearInterval(interval);
        }
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isProcessing, timer]);

    const validateTokenAndGetCredits = async (tokenToCheck) => {
        try {
            const result = await checkToken(tokenToCheck);
            if (result.isValid) {
                setIsTokenValid(true);
                setCredits(result.credits);
            } else {
                setIsTokenValid(false);
                removeToken();
            }
        } catch (error) {
            console.error('Error checking token:', error);
            setIsTokenValid(false);
            removeToken();
        }
    };

    const handleTokenSubmit = async (newToken) => {
        setLoading(true);
        try {
            const result = await checkToken(newToken);
            if (result.isValid) {
                setToken(newToken);
                setIsTokenValid(true);
                setCredits(result.credits);
                saveToken(newToken);
            } else {
                toast.error(result.errorMessage || 'Invalid token. Please try again.');
            }
        } catch (error) {
            toast.error('Error validating token. Please try again.');
        }
        setLoading(false);
    };

    const handleInputChange = (values) => {
        setInputValues(values);
    };

    const handleSubmit = async (values) => {
        if (isProcessing) return;
        
        // Check if all required inputs have values
        const requiredInputs = currentApp.inputs.filter(input => input.required);
        const allRequiredFilled = requiredInputs.every(input => 
            values[input.key] && values[input.key].trim()
        );
        
        if (!allRequiredFilled) {
            toast.error('Please fill in all required fields.');
            return;
        }
        
        setIsProcessing(true);
        setResponse(null);
        setTimer(currentApp.estimatedTime);
        
        try {
            const currentCredits = credits;
            const result = await processQuery(token, values);
            
            // Get updated credits
            const latestTokenInfo = await checkToken(token);
            
            if (latestTokenInfo.isValid) {
                const used = currentCredits - latestTokenInfo.credits;
                setCreditsUsed(used > 0 ? used : currentApp.credits);
                setCredits(latestTokenInfo.credits);
            }
            
            setResponse(result);
        } catch (error) {
            console.error('Error processing request:', error);
            setError(error.message || 'An error occurred. Please try again.');
        } finally {
            setIsProcessing(false);
            setTimer(0);
        }
    };

    const AppFooter = ({ sampleUrl }) => {
        return (
            <div className="mt-4 pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center text-xs text-gray-500">
                    <a
                        href="https://apilabz.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center hover:text-blue-500"
                    >
                        <HomeIcon className="h-4 w-4 mb-1" />
                        <span>API Labz</span>
                    </a>
                    
                    <a
                        href="https://apilabz.com/api-labs/dashboard"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center hover:text-blue-500"
                    >
                        <ChartBarIcon className="h-4 w-4 mb-1" />
                        <span>Activity</span>
                    </a>
                    
                    <a
                        href={sampleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center hover:text-blue-500"
                    >
                        <DocumentIcon className="h-4 w-4 mb-1" />
                        <span>Sample</span>
                    </a>
                    
                    <a
                        href="mailto:info@apilabz.com"
                        className="flex flex-col items-center hover:text-blue-500"
                    >
                        <EnvelopeIcon className="h-4 w-4 mb-1" />
                        <span>Contact</span>
                    </a>
                </div>
            </div>
        );
    };

    if (loading) {
        return <LoadingProgress />;
    }

    return (
        <div className="container p-2 max-w-md mx-auto flex flex-col" style={{ minHeight: '480px' }}>
            <ToastContainer position="top-center" />
            
            <AppHeader 
                appName={currentApp.name} 
                appIcon={currentApp.icon} 
                credits={credits} 
            />
            
            {!isTokenValid ? (
                <div className="mt-3 mb-3 flex-grow">
                    <TokenInputModal onSubmit={handleTokenSubmit} />
                </div>
            ) : (
                <div className="my-3 flex-grow">
                    <AppSearch 
                        app={currentApp}
                        values={inputValues}
                        onChange={handleInputChange}
                        onSubmit={handleSubmit}
                        isProcessing={isProcessing}
                        timer={timer}
                    />
                    
                    {response && (
                        <ResultDisplay 
                            response={response} 
                            responseType={currentApp.responseType}
                            creditsUsed={creditsUsed}
                        />
                    )}
                </div>
            )}
            
            <AppFooter 
                sampleUrl={currentApp.sampleUrl}
            />
            
            {error && (
                <ErrorDialog 
                    error={error} 
                    onClose={() => setError('')} 
                />
            )}
        </div>
    );
};

export default MainComponent;